<script setup>
import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue';
import { router } from '@inertiajs/vue3';
import Card from 'primevue/card';
import Button from 'primevue/button';
import { useToast } from 'primevue/usetoast';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();

const props = defineProps({
    orderNumber: { type: String, required: true },
    qrCodeBase64: { type: String, default: null },
    qrCodeText: { type: String, default: null },
    expirationDate: { type: String, default: null },
    total: { type: Number, required: true },
    currency: { type: String, required: true },
    orderId: { type: Number, required: true },
});

const timeLeft = ref('');
let timer = null;

const formatTotal = () => {
    const value = props.total / 100;
    return new Intl.NumberFormat('pt-BR', {
        style: 'currency',
        currency: props.currency,
        minimumFractionDigits: 2,
    }).format(value);
};

const updateCountdown = () => {
    if (!props.expirationDate) {
        timeLeft.value = '';
        return;
    }

    const expiry = new Date(props.expirationDate).getTime();
    const now = Date.now();
    const diff = expiry - now;

    if (diff <= 0) {
        timeLeft.value = $t('Expired');
        clearInterval(timer);
        return;
    }

    const hours = Math.floor(diff / 3600000);
    const minutes = Math.floor((diff % 3600000) / 60000);
    const seconds = Math.floor((diff % 60000) / 1000);

    if (hours > 0) {
        timeLeft.value = `${hours}h ${String(minutes).padStart(2, '0')}min`;
    } else {
        timeLeft.value = `${minutes}min ${String(seconds).padStart(2, '0')}s`;
    }
};

onMounted(() => {
    updateCountdown();
    timer = setInterval(updateCountdown, 1000);
});

onUnmounted(() => clearInterval(timer));

const isExpired = computed(() => timeLeft.value === $t('Expired'));

const copy = async (text, label) => {
    try {
        await navigator.clipboard.writeText(text);
        toast.add({ severity: 'success', summary: $t('Copied'), detail: `${label} ${$t('copied to clipboard')}`, life: 2000 });
    } catch {
        toast.add({ severity: 'warn', summary: $t('Error'), detail: $t('Could not copy'), life: 2000 });
    }
};

const goToOrder = () => {
    router.visit(proxy.route('client.orders.show', { order: props.orderId }));
};
</script>

<template>
    <Card class="pix-instructions">
        <template #title>
            <div class="flex items-center gap-2">
                <i class="pi pi-qrcode text-green-600 text-xl"></i>
                <span>{{ $t('Pay with Pix') }}</span>
            </div>
        </template>

        <template #content>
            <p class="text-surface-600 mb-5">
                {{ $t('Scan the QR code below or copy the Pix code to complete your payment. Your order will be activated automatically.') }}
            </p>

            <!-- QR Code + valor -->
            <div class="border border-green-200 rounded-xl p-5 bg-green-50 mb-5 flex flex-col items-center gap-4">
                <img
                    v-if="qrCodeBase64"
                    :src="`data:image/png;base64,${qrCodeBase64}`"
                    alt="Pix QR Code"
                    class="w-48 h-48 rounded-lg"
                />
                <div
                    v-else
                    class="w-48 h-48 rounded-lg bg-surface-100 flex items-center justify-center text-surface-400 text-sm text-center p-4"
                >
                    <span>{{ $t('QR code will be available shortly') }}</span>
                </div>

                <div class="text-center">
                    <div class="text-sm text-surface-500 mb-1">{{ $t('Amount') }}</div>
                    <div class="text-2xl font-bold text-green-700">{{ formatTotal() }}</div>
                </div>

                <!-- Countdown -->
                <div
                    v-if="timeLeft"
                    class="flex items-center gap-2 text-sm px-3 py-1.5 rounded-full"
                    :class="isExpired ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'"
                >
                    <i class="pi pi-clock text-xs"></i>
                    <span v-if="!isExpired">{{ $t('Expires in') }} {{ timeLeft }}</span>
                    <span v-else>{{ $t('Expired') }}</span>
                </div>
            </div>

            <!-- Copia e cola -->
            <div v-if="qrCodeText" class="bg-surface-50 border border-surface-200 rounded-xl p-4 mb-5">
                <div class="text-sm font-medium text-surface-600 mb-2">{{ $t('Or copy the Pix code') }}</div>
                <div class="flex items-center gap-2">
                    <code class="flex-1 text-xs bg-white border border-surface-200 rounded-lg p-3 break-all font-mono text-surface-700 select-all">
                        {{ qrCodeText }}
                    </code>
                </div>
                <Button
                    :label="$t('Copy Pix Code')"
                    icon="pi pi-copy"
                    class="w-full mt-3"
                    severity="secondary"
                    @click="copy(qrCodeText, $t('Pix code'))"
                />
            </div>

            <!-- Info -->
            <div class="bg-surface-50 rounded-lg p-4 text-sm text-surface-600 space-y-2 mb-5">
                <p><i class="pi pi-bolt text-green-500 mr-2"></i>{{ $t('Payment is confirmed instantly after Pix is processed.') }}</p>
                <p><i class="pi pi-envelope text-blue-500 mr-2"></i>{{ $t('You will receive a confirmation email when the payment is processed.') }}</p>
            </div>

            <Button
                :label="$t('View Order Status')"
                icon="pi pi-arrow-right"
                class="w-full"
                @click="goToOrder"
            />
        </template>
    </Card>
</template>
